import { useUserSignedIn } from '@sketch/modules-common'
import { IS_EMBEDDED } from '@sketch/constants'

import {
  useGetWorkspaceQuery,
  useGetWorkspacesForDuplicationQuery,
  VersionFragment,
} from '@sketch/gql-types'
import { getDownloadProps } from '../utils'
import { useDuplicateShareToDrafts } from './useDuplicateShareToDrafts'
import { ShareWithoutVersion } from '../../versioning'
import { useCanDuplicateToWorkspace } from '../../workspace/hooks'

interface UseDeriveDownloadActionProps {
  share: ShareWithoutVersion
  currentVersion: VersionFragment | null
}

export const useHeaderActionDeriver = ({
  share,
  currentVersion,
}: UseDeriveDownloadActionProps) => {
  const isSignedIn = useUserSignedIn()

  const { data: workspaceData, loading: loadingWorkspace } =
    useGetWorkspaceQuery({
      variables: {
        identifier: share.workspace.identifier,
      },
      skip: !share.workspace.userIsMember,
    })

  const {
    data,
    loading: loadingWorkspacesForDuplication,
    networkStatus,
  } = useGetWorkspacesForDuplicationQuery({
    skip: !isSignedIn,
  })

  const workspaces = data?.me.workspaces ?? []

  const { data: canDuplicateData } = useCanDuplicateToWorkspace(
    share.workspace.identifier,
    share.publicAccessLevel,
    share.userCanInspect
  )
  const canDuplicate = canDuplicateData?.canDuplicate

  const [duplicateShare] = useDuplicateShareToDrafts({
    share,
    versionIdentifier: currentVersion?.identifier ?? '',
    workspaceIdentifier: share.workspace.identifier,
  })

  const isEditor = workspaceData?.workspace.userCanEdit
  const download = getDownloadProps(currentVersion)
  const isLoading = loadingWorkspace || loadingWorkspacesForDuplication

  const isViewerWithoutEligibleWorkspaces =
    isSignedIn && !isEditor && workspaces.length === 0 && networkStatus === 7

  const showLibraryButton = share.shareType === 'LIBRARY'

  const showDownloadButton =
    !showLibraryButton &&
    // Anonymous users see "Download" button
    ((!isSignedIn && download?.isAvailable) ||
      // Viewers without eligible Workspaces to duplicate too see the "Download" button
      (isViewerWithoutEligibleWorkspaces && download?.isAvailable))

  const showRemixButton =
    !showLibraryButton &&
    // Signed in, non-Editors with eligible Workspaces to duplicate too see the "Remix" button
    isSignedIn &&
    !isEditor &&
    canDuplicate &&
    !IS_EMBEDDED

  return {
    showLibraryButton,
    showDownloadButton,
    showRemixButton,
    isLoading,
    duplicateShare,
    download,
  }
}
